import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';
import styles from './MenuUpload-Page.module.css';
import { getMenuJobsListAPI, getMenuStatusAPI } from '../../context-api/api-services/MenuUpload.service';

const STORAGE_KEYS = {
    selectedJob: 'menu_upload_selected_job_v1',
    jobsCache: 'menu_upload_jobs_cache_v1',
};

const UI_STATE = {
    IDLE: 'idle',
    PROCESSING: 'processing',
    COMPLETED: 'completed',
    ERROR: 'error',
};

const LOOP_STEPS = ['generated_meta_data', 'build_payload', 'validate_payload'];

const STEP_LABELS = {
    started: 'Starting Job',
    parsed: 'Parsing Menu',
    looped: 'Processing Dishes',
    generated_meta_data: 'Generating Metadata',
    build_payload: 'Building Dish Payload',
    validate_payload: 'Validating Payload',
    aggregate_dishes: 'Aggregating Dishes',
    send_email: 'Sending Completion Email',
    complete: 'Complete',
};

function getStepLabel(currentStep) {
    if (!currentStep) return 'Processing…';
    return (
        STEP_LABELS[currentStep] ||
        String(currentStep).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    );
}

const PIPELINE_STEPS = [
    { id: 'webhook',   label: 'Upload Webhook',    icon: '🪝', phase: 'pre',  match: ['started', 'webhook', 'upload'] },
    { id: 'parse',     label: 'Parse Menu',        icon: '🧩', phase: 'pre',  match: ['parsed', 'parse', 'json'] },
    { id: 'loop',      label: 'Loop Over Dishes',  icon: '🔁', phase: 'pre',  match: ['looped', 'loop'] },
    { id: 'meta',      label: 'Generate Metadata', icon: '🧠', phase: 'loop', match: ['generated_meta_data', 'meta'] },
    { id: 'build',     label: 'Build Dish Payload', icon: '📦', phase: 'loop', match: ['build_payload', 'build'] },
    { id: 'validate',  label: 'Validate Payload',  icon: '✅', phase: 'loop', match: ['validate_payload', 'validate'] },
    { id: 'aggregate', label: 'Aggregate Dishes',  icon: '🗂️', phase: 'post', match: ['aggregate_dishes', 'aggregate'] },
    { id: 'email',     label: 'Send Email',        icon: '📨', phase: 'post', match: ['send_email', 'email'] },
    { id: 'complete',  label: 'Completed',         icon: '🎉', phase: 'post', match: ['complete', 'completed', 'done', 'success'] },
];

function unwrapApiResponse(json) {
    if (json && typeof json === 'object' && 'success' in json) return json.data ?? null;
    return json;
}

function extractJobsArrayFromListResponse(responseData) {
    const inner = unwrapApiResponse(responseData);
    if (Array.isArray(inner)) return inner;
    if (inner && typeof inner === 'object' && (inner.job_id || inner.jobId)) return [inner];
    if (inner && Array.isArray(inner.data)) return inner.data;
    if (inner && Array.isArray(inner.jobs)) return inner.jobs;
    return [];
}

function clampProgress(value) {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return 0;
    return Math.max(0, Math.min(100, numeric));
}

function parseNumber(input) {
    const numeric = Number(input);
    return Number.isNaN(numeric) ? null : numeric;
}

function isFailureStatus(status) {
    if (!status) return false;
    return /failed|error/i.test(String(status));
}

function isCompleteStatus(status) {
    if (!status) return false;
    return /completed|complete|done|success/i.test(String(status));
}

function stepIndexFromText(currentStep) {
    if (!currentStep) return -1;
    const normalized = String(currentStep).toLowerCase().trim();
    if (!normalized) return -1;

    for (let i = 0; i < PIPELINE_STEPS.length; i += 1) {
        const step = PIPELINE_STEPS[i];
        if (!step.match || !Array.isArray(step.match)) continue;
        if (step.match.some((m) => normalized.includes(String(m).toLowerCase()))) return i;
    }

    const exactIndex = PIPELINE_STEPS.findIndex((step) => step.label.toLowerCase() === normalized);
    if (exactIndex >= 0) return exactIndex;

    return PIPELINE_STEPS.findIndex((step) => normalized.includes(step.label.toLowerCase().split(' ')[0]));
}

function deriveKitchenStatus(jobPayload) {
    if (!jobPayload || !jobPayload.job_id) return 'idle';
    if (isFailureStatus(jobPayload.status) || jobPayload.error === true) return 'failed';
    if (typeof jobPayload.error === 'string' && jobPayload.error.trim()) return 'failed';
    if (isCompleteStatus(jobPayload.status) || clampProgress(jobPayload.progress) >= 100) return 'completed';
    return 'processing';
}

function normalizeJobPayload(input, fallbackKitchenName = '') {
    if (!input) return null;
    const source = Array.isArray(input) ? input[0] : input;
    if (!source || typeof source !== 'object') return null;

    const status = source.status || source.state || source.job_status || '';
    const currentStep = source.currentStep || source.current_step || source.step || status || '';
    const progress = clampProgress(source.progress);
    const errorStr = typeof source.error === 'string' ? source.error.trim() : '';
    const msg =
        typeof source.message === 'string' && source.message.trim()
            ? source.message
            : errorStr || '';

    const result =
        source.result != null && typeof source.result === 'object' ? source.result : null;

    return {
        kitchenName:
            source.kitchen_name ||
            source.kitchenName ||
            source.kitchen_id ||
            source.kitchenId ||
            fallbackKitchenName ||
            '',
        job_id: source.job_id || source.jobId || source._id || '',
        recordId: source._id || source.job_id || '',
        adminId: source.admin_id || source.adminId || '',
        lastSeq: source.lastSeq != null ? source.lastSeq : null,
        status: status || 'idle',
        currentStep,
        progress,
        currentItem: parseNumber(source.currentItem ?? source.current_item),
        totalItems: parseNumber(source.totalItems ?? source.total_items),
        error: source.error === true || Boolean(errorStr),
        message: msg,
        createdAt: source.createdAt ?? source.created_at ?? null,
        updatedAt: source.updatedAt ?? source.updated_at ?? null,
        result,
    };
}

function statusLabel(status) {
    if (status === 'completed') return 'Completed';
    if (status === 'processing') return 'Processing';
    if (status === 'failed') return 'Failed';
    return 'Idle';
}

function RippleLoader({ compact = false }) {
    return (
        <div className={`${styles.rippleLoader} ${compact ? styles.rippleLoaderCompact : ''}`} aria-hidden>
            <span />
            <span />
            <span />
            <strong>AI</strong>
        </div>
    );
}

function StatusBadge({ status }) {
    return (
        <span className={`${styles.statusBadge} ${styles[`statusBadge${status}`]}`}>
            <span className={styles.statusDot} />
            {statusLabel(status)}
        </span>
    );
}

function shortJobId(id) {
    if (!id) return '';
    const s = String(id);
    return s.length < 14 ? s : `${s.slice(0, 6)}…${s.slice(-4)}`;
}

function shallowEqualJobRow(a, b) {
    if (a === b) return true;
    if (!a || !b) return false;
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const k of keys) {
        if (a[k] !== b[k]) return false;
    }
    return true;
}

function JobsSidebar({ jobs, selectedJobId, onJobSelect, connectionStatus, listLoading }) {
    const listRef = useRef(null);
    const itemRefs = useRef({});
    const [indicator, setIndicator] = useState({ top: 0, height: 0, opacity: 0 });

    const recomputeIndicator = useCallback(() => {
        const selectedEl = selectedJobId ? itemRefs.current[selectedJobId] : null;
        if (!selectedEl) return;

        requestAnimationFrame(() => {
            const nextTop = selectedEl.offsetTop;
            const nextHeight = selectedEl.offsetHeight;
            setIndicator({ top: nextTop, height: nextHeight, opacity: 1 });
        });
    }, [selectedJobId]);

    useLayoutEffect(() => {
        recomputeIndicator();
    }, [jobs, selectedJobId, recomputeIndicator]);

    useEffect(() => {
        const onResize = () => recomputeIndicator();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [recomputeIndicator]);

    return (
        <aside className={styles.sidebar}>
            <div className={styles.sidebarHeader}>
                <p className={styles.kicker}>GET /jobs/list</p>
                <h2 className={styles.sidebarTitle}>Jobs</h2>
                <p className={styles.kitchenMeta} style={{ margin: '6px 0 0', fontSize: '12px' }}>
                    {listLoading ? 'Loading…' : `${jobs.length} job${jobs.length === 1 ? '' : 's'}`}
                </p>
            </div>

            <div ref={listRef} className={styles.sidebarList}>
                <div
                    className={styles.activeKitchenIndicator}
                    style={{
                        transform: `translateY(${indicator.top}px)`,
                        height: indicator.height,
                        opacity: indicator.opacity,
                    }}
                    aria-hidden
                />
                {listLoading && (
                    <p className={styles.kitchenMeta} style={{ padding: '12px 8px' }}>
                        Fetching jobs…
                    </p>
                )}
                {!listLoading && jobs.length === 0 && (
                    <p className={styles.kitchenMeta} style={{ padding: '12px 8px' }}>
                        No jobs yet. Upload a menu to create one.
                    </p>
                )}
                {!listLoading &&
                    jobs.map((row) => {
                        const jid = row.job_id || row._id || '';
                        const norm = normalizeJobPayload(row, '');
                        const badge = deriveKitchenStatus(norm);
                        const kitchenLabel = row.kitchen_id || row.kitchen_name || 'Kitchen';
                        return (
                            <button
                                key={jid}
                                type='button'
                                onClick={() => onJobSelect(jid)}
                                ref={(el) => {
                                    itemRefs.current[jid] = el;
                                }}
                                className={`${styles.kitchenItem} ${selectedJobId === jid ? styles.kitchenItemActive : ''}`}
                            >
                                <div style={{ textAlign: 'left', minWidth: 0 }}>
                                    <p className={styles.kitchenName}>{kitchenLabel}</p>
                                    <p
                                        className={styles.kitchenMeta}
                                        style={{ marginTop: '4px', fontSize: '11px', wordBreak: 'break-all' }}
                                    >
                                        {shortJobId(jid)}
                                    </p>
                                </div>
                                <StatusBadge status={badge} />
                            </button>
                        );
                    })}
            </div>

            <div className={styles.sidebarFooter}>
                <span
                    className={`${styles.connectionBadge} ${connectionStatus === 'socket' ? styles.connectionBadgeLive : styles.connectionBadgeFallback
                        }`}
                >
                    {connectionStatus === 'socket' ? 'Socket live' : 'Polling fallback'}
                </span>
            </div>
        </aside>
    );
}

const FLOW_EDGES = [
    { from: 'loop',     to: 'ai_agent',  fromPort: 'right',  toPort: 'left',   type: 'main' },
    { from: 'ai_agent', to: 'aggregate', fromPort: 'right',  toPort: 'left',   type: 'main' },
    { from: 'ai_agent', to: 'meta',      fromPort: 'bottom', toPort: 'top',    type: 'tool' },
    { from: 'ai_agent', to: 'build',     fromPort: 'bottom', toPort: 'top',    type: 'tool' },
    { from: 'ai_agent', to: 'validate',  fromPort: 'bottom', toPort: 'top',    type: 'tool' },
];

function getNodePort(el, wrapRect, port) {
    if (!el) return null;
    const r = el.getBoundingClientRect();
    const x = r.left - wrapRect.left;
    const y = r.top  - wrapRect.top;
    switch (port) {
        case 'left':   return { x,              y: y + r.height / 2 };
        case 'right':  return { x: x + r.width, y: y + r.height / 2 };
        case 'top':    return { x: x + r.width / 2, y };
        case 'bottom': return { x: x + r.width / 2, y: y + r.height };
        default:       return { x: x + r.width / 2, y: y + r.height / 2 };
    }
}

function buildEdgePath(from, to, isTool) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    if (Math.abs(dy) < 2) return `M ${from.x},${from.y} H ${to.x}`;
    if (Math.abs(dx) < 2) return `M ${from.x},${from.y} V ${to.y}`;
    if (isTool) {
        const midY = from.y + dy * 0.45;
        return `M ${from.x},${from.y} V ${midY} H ${to.x} V ${to.y}`;
    }
    const midX = from.x + dx / 2;
    return `M ${from.x},${from.y} H ${midX} V ${to.y} H ${to.x}`;
}

function NFlowEdge({ edge }) {
    const d = buildEdgePath(edge.from, edge.to, edge.type === 'tool');
    const isTool = edge.type === 'tool';
    const markerId = edge.stateClass === styles.nEdgeDone
        ? 'nArrowDone'
        : edge.stateClass === styles.nEdgeActive
          ? 'nArrowActive'
          : 'nArrowPending';
    return (
        <path
            className={`${styles.nEdge} ${edge.stateClass}${isTool ? ` ${styles.nEdgeTool}` : ''}`}
            d={d}
            markerEnd={`url(#${markerId})`}
        />
    );
}

function FlowStepCard({ step, isCompleted, isActive, setRef }) {
    const stateClass = isCompleted ? styles.nNodeDone : isActive ? styles.nNodeActive : styles.nNodePending;
    return (
        <div ref={setRef} className={`${styles.nNode} ${stateClass}`} role='listitem'>
            <div className={styles.nNodeIcon} aria-hidden>
                <span>{step.icon}</span>
                {isCompleted && <span className={styles.nNodeCheck}>✓</span>}
            </div>
            <div className={styles.nNodeBody}>
                <span className={styles.nNodeLabel}>{step.label}</span>
            </div>
            {isActive && <span className={styles.nNodePulse} aria-hidden />}
        </div>
    );
}

function AIAgentCard({ setRef, isActive, isDone, currentItem, totalItems }) {
    const hasCounter = isActive && currentItem != null && totalItems != null && totalItems > 0;
    const subtitle = hasCounter
        ? `Item ${currentItem} of ${totalItems}`
        : isDone
          ? 'All items processed'
          : 'Processing Orchestrator';

    const itemPct = hasCounter ? Math.round((currentItem / totalItems) * 100) : null;

    return (
        <div
            ref={setRef}
            className={`${styles.nAgent} ${isActive ? styles.nAgentActive : isDone ? styles.nAgentDone : ''}`}
        >
            <div className={styles.nAgentIconWrap} aria-hidden>
                <span className={styles.nAgentEmoji}>🤖</span>
            </div>
            <div className={styles.nAgentBody}>
                <p className={styles.nAgentTitle}>AI Agent</p>
                <p className={styles.nAgentSub}>{subtitle}</p>
                {itemPct !== null && (
                    <div className={styles.nAgentProgressTrack}>
                        <div className={styles.nAgentProgressFill} style={{ width: `${itemPct}%` }} />
                    </div>
                )}
            </div>
            {isActive && <span className={styles.nAgentPulse} aria-hidden />}
        </div>
    );
}

function NVConn({ done, active }) {
    const cls = done ? styles.nVConnDone : active ? styles.nVConnActive : styles.nVConnPending;
    return <div className={`${styles.nVConn} ${cls}`} aria-hidden />;
}

function NHConn({ done, active }) {
    const cls = done ? styles.nHConnDone : active ? styles.nHConnActive : styles.nHConnPending;
    return <div className={`${styles.nHConn} ${cls}`} aria-hidden />;
}

const LOOP_PHASE_DURATION_MS = 420;

function ProcessNodeFlow({ currentStep, progress, currentItem, totalItems }) {
    const activeIndex = useMemo(() => {
        if (progress >= 100) return PIPELINE_STEPS.length - 1;
        const matchedIndex = stepIndexFromText(currentStep);
        if (matchedIndex >= 0) {
            if (matchedIndex === PIPELINE_STEPS.length - 1) return PIPELINE_STEPS.length - 2;
            return matchedIndex;
        }
        const derived = Math.floor((clampProgress(progress) / 100) * (PIPELINE_STEPS.length - 1));
        return Math.max(0, Math.min(PIPELINE_STEPS.length - 2, derived));
    }, [currentStep, progress]);

    const baseStepStates = useMemo(() => {
        const map = {};
        PIPELINE_STEPS.forEach((step, index) => {
            const isCompleted = progress >= 100 || index < activeIndex;
            const isActive    = progress < 100 && index === activeIndex;
            map[step.id] = { isCompleted, isActive, isPending: !isCompleted && !isActive };
        });
        return map;
    }, [activeIndex, progress]);

    const preSteps  = useMemo(() => PIPELINE_STEPS.filter((s) => s.phase === 'pre'),  []);
    const loopSteps = useMemo(() => PIPELINE_STEPS.filter((s) => s.phase === 'loop'), []);
    const postSteps = useMemo(() => PIPELINE_STEPS.filter((s) => s.phase === 'post'), []);

    /** When currentItem ticks up while a loop is in progress, animate meta→build→validate. */
    const [loopAnimPhase, setLoopAnimPhase] = useState(null); // null | 0 | 1 | 2
    const prevItemRef   = useRef(currentItem);
    const animTimersRef = useRef([]);

    useEffect(() => {
        const isInLoop = LOOP_STEPS.some((s) =>
            String(currentStep || '').toLowerCase().includes(s.toLowerCase()),
        );
        const itemChanged = currentItem != null && currentItem !== prevItemRef.current;
        prevItemRef.current = currentItem;

        if (!isInLoop || !itemChanged || progress >= 100) return;

        animTimersRef.current.forEach(clearTimeout);
        animTimersRef.current = [];

        setLoopAnimPhase(0);
        const t1 = setTimeout(() => setLoopAnimPhase(1), LOOP_PHASE_DURATION_MS);
        const t2 = setTimeout(() => setLoopAnimPhase(2), LOOP_PHASE_DURATION_MS * 2);
        const t3 = setTimeout(() => setLoopAnimPhase(null), LOOP_PHASE_DURATION_MS * 3);
        animTimersRef.current = [t1, t2, t3];

        return () => animTimersRef.current.forEach(clearTimeout);
    }, [currentItem, currentStep, progress]);

    /** Merge animation overrides into loop step states. */
    const stepStates = useMemo(() => {
        if (loopAnimPhase === null) return baseStepStates;
        const map = { ...baseStepStates };
        loopSteps.forEach((step, idx) => {
            const isCompleted = idx < loopAnimPhase;
            const isActive    = idx === loopAnimPhase;
            map[step.id] = { isCompleted, isActive, isPending: !isCompleted && !isActive };
        });
        return map;
    }, [baseStepStates, loopAnimPhase, loopSteps]);

    const isLoopActive = loopAnimPhase !== null || loopSteps.some((s) => stepStates[s.id]?.isActive);
    const isLoopDone   = progress >= 100 || (loopAnimPhase === null && loopSteps.every((s) => stepStates[s.id]?.isCompleted));

    const nodeRefs = useRef({});
    const wrapRef  = useRef(null);
    const [edges, setEdges] = useState([]);

    const computeEdges = useCallback(() => {
        const wrapEl = wrapRef.current;
        if (!wrapEl) return;
        const wrapRect = wrapEl.getBoundingClientRect();

        const computed = FLOW_EDGES.map((e) => {
            const from = getNodePort(nodeRefs.current[e.from], wrapRect, e.fromPort);
            const to   = getNodePort(nodeRefs.current[e.to],   wrapRect, e.toPort);
            if (!from || !to) return null;

            let stateClass = styles.nEdgePending;
            if (e.from === 'loop' && e.to === 'ai_agent') {
                const s = stepStates['loop'];
                if (s?.isCompleted) stateClass = styles.nEdgeDone;
                else if (s?.isActive) stateClass = styles.nEdgeActive;
            } else if (e.from === 'ai_agent' && e.to === 'aggregate') {
                const s = stepStates['aggregate'];
                if (s?.isCompleted) stateClass = styles.nEdgeDone;
                else if (s?.isActive) stateClass = styles.nEdgeActive;
                else if (isLoopDone && !isLoopActive) stateClass = styles.nEdgeActive;
            } else if (e.type === 'tool') {
                const s = stepStates[e.to];
                if (s?.isCompleted) stateClass = styles.nEdgeDone;
                else if (s?.isActive) stateClass = styles.nEdgeActive;
            }

            return { key: `${e.from}-${e.to}`, from, to, type: e.type, stateClass };
        }).filter(Boolean);

        setEdges(computed);
    }, [stepStates, isLoopActive, isLoopDone]);

    useLayoutEffect(() => { computeEdges(); }, [computeEdges]);
    useEffect(() => {
        window.addEventListener('resize', computeEdges);
        return () => window.removeEventListener('resize', computeEdges);
    }, [computeEdges]);

    const safeItem  = currentItem != null ? Number(currentItem)  : null;
    const safeTotal = totalItems  != null ? Number(totalItems)   : null;

    return (
        <div className={styles.nFlowGraph} ref={wrapRef} aria-label='AI processing pipeline'>
            <svg className={styles.nFlowSvg} aria-hidden>
                <defs>
                    <marker id='nArrowPending' markerWidth='8' markerHeight='6' refX='7' refY='3' orient='auto'>
                        <polygon points='0 0, 8 3, 0 6' fill='rgba(148,163,184,0.6)' />
                    </marker>
                    <marker id='nArrowDone' markerWidth='8' markerHeight='6' refX='7' refY='3' orient='auto'>
                        <polygon points='0 0, 8 3, 0 6' fill='rgba(34,197,94,0.85)' />
                    </marker>
                    <marker id='nArrowActive' markerWidth='8' markerHeight='6' refX='7' refY='3' orient='auto'>
                        <polygon points='0 0, 8 3, 0 6' fill='rgb(59,130,246)' />
                    </marker>
                </defs>
                {edges.map((edge) => <NFlowEdge key={edge.key} edge={edge} />)}
            </svg>

            <div className={styles.nFlowLayout}>
                <div className={styles.nPreCol}>
                    {preSteps.map((step, idx) => {
                        const state = stepStates[step.id];
                        return (
                            <React.Fragment key={step.id}>
                                <FlowStepCard
                                    step={step}
                                    isCompleted={state.isCompleted}
                                    isActive={state.isActive}
                                    setRef={(el) => { nodeRefs.current[step.id] = el; }}
                                />
                                {idx < preSteps.length - 1 && (
                                    <NVConn done={state.isCompleted} active={state.isActive} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>

                <div className={styles.nAgentCol}>
                    <AIAgentCard
                        setRef={(el) => { nodeRefs.current['ai_agent'] = el; }}
                        isActive={isLoopActive}
                        isDone={isLoopDone}
                        currentItem={safeItem}
                        totalItems={safeTotal}
                    />
                    <div className={styles.nToolGap} aria-hidden />
                    <div className={styles.nLoopRow}>
                        {loopSteps.map((step, idx) => {
                            const state = stepStates[step.id];
                            return (
                                <React.Fragment key={step.id}>
                                    <FlowStepCard
                                        step={step}
                                        isCompleted={state.isCompleted}
                                        isActive={state.isActive}
                                        setRef={(el) => { nodeRefs.current[step.id] = el; }}
                                    />
                                    {idx < loopSteps.length - 1 && (
                                        <NHConn done={state.isCompleted} active={state.isActive} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                    <div className={`${styles.nLoopLabel} ${isLoopActive ? styles.nLoopLabelActive : isLoopDone ? styles.nLoopLabelDone : ''}`}>
                        {isLoopActive ? '🔄 Running loop…' : isLoopDone ? '✓ Loop complete' : '🔄 Per-item loop'}
                    </div>
                </div>

                <div className={styles.nPostCol}>
                    {postSteps.map((step, idx) => {
                        const state = stepStates[step.id];
                        return (
                            <React.Fragment key={step.id}>
                                <FlowStepCard
                                    step={step}
                                    isCompleted={state.isCompleted}
                                    isActive={state.isActive}
                                    setRef={(el) => { nodeRefs.current[step.id] = el; }}
                                />
                                {idx < postSteps.length - 1 && (
                                    <NVConn done={state.isCompleted} active={state.isActive} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function ProcessingPanel({ job, connectionStatus }) {
    const isLoopStep = LOOP_STEPS.includes(job.currentStep);
    const hasItemProgress =
        isLoopStep &&
        job.currentItem != null &&
        job.totalItems != null &&
        Number(job.totalItems) > 0;

    return (
        <div className={styles.panelCard}>
            <div className={styles.panelHeaderRow}>
                <div>
                    <p className={styles.kicker}>Processing State</p>
                    <h3 className={styles.panelTitle}>AI Processing</h3>
                </div>
                <span
                    className={`${styles.connectionBadge} ${connectionStatus === 'socket' ? styles.connectionBadgeLive : styles.connectionBadgeFallback
                        }`}
                >
                    {connectionStatus === 'socket' ? 'Socket live' : 'Polling fallback'}
                </span>
            </div>

            <div className={styles.processingHero}>
                <RippleLoader compact />
                <div>
                    <p className={styles.processingStep}>{getStepLabel(job.currentStep)}</p>
                    {hasItemProgress ? (
                        <p className={styles.processingSubtext}>
                            Item {job.currentItem} of {job.totalItems}
                        </p>
                    ) : (
                        <p className={styles.processingSubtext}>AI is analyzing your menu</p>
                    )}
                </div>
            </div>

            <div className={styles.progressBlock}>
                <div className={styles.progressHeader}>
                    <span>Progress</span>
                    <strong>{Math.round(job.progress)}%</strong>
                </div>
                <div className={styles.progressTrack}>
                    <span className={styles.progressFill} style={{ width: `${clampProgress(job.progress)}%` }} />
                </div>
            </div>

            <ProcessNodeFlow
                currentStep={job.currentStep}
                progress={job.progress}
                currentItem={job.currentItem}
                totalItems={job.totalItems}
            />
        </div>
    );
}

function formatMaybeDate(value) {
    if (value == null || value === '') return '—';
    try {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
    } catch {
        return String(value);
    }
}

function formatMoney(n) {
    if (n == null || n === '') return '—';
    const x = Number(n);
    return Number.isNaN(x) ? String(n) : `$${x.toFixed(2)}`;
}

function JsonishValue({ value }) {
    if (value == null) return '—';
    if (Array.isArray(value)) {
        return value.length ? value.join(', ') : '—';
    }
    if (typeof value === 'object') {
        return (
            <pre className={styles.resultJsonBlock}>{JSON.stringify(value, null, 2)}</pre>
        );
    }
    return String(value);
}

function CompletedPanel({ job, onReset }) {
    const bulk = job?.result?.bulk_products;
    const items = Array.isArray(bulk?.items) ? bulk.items : [];
    const failedItems = Array.isArray(bulk?.failed) ? bulk.failed : (Array.isArray(bulk?.failures) ? bulk.failures : []);
    const totalItems = job?.totalItems ?? bulk?.count ?? items.length;
    const customMessage = bulk?.CustomMessage ?? bulk?.customMessage;
    const hasCustomMessage =
        customMessage != null &&
        (typeof customMessage !== 'string' || String(customMessage).trim() !== '');
    const [activeTab, setActiveTab] = useState('inserted');

    return (
        <div className={`${styles.panelCard} ${styles.completedPanel}`}>
            <div className={styles.completedHero}>
                <div className={`${styles.stateIcon} ${styles.stateIconSuccess}`}>✓</div>
                <div>
                    <p className={styles.centerStateTitle} style={{ margin: 0 }}>
                        Menu processed successfully
                    </p>
                    <p className={styles.centerStateSub} style={{ margin: '6px 0 0' }}>
                        {totalItems
                            ? `${totalItems} dish${totalItems === 1 ? '' : 'es'} processed successfully.`
                            : 'Your menu is now ready.'}
                    </p>
                </div>
            </div>

            {hasCustomMessage && (
                <div className={styles.bulkCustomMessage} role='status'>
                    <h4 className={styles.resultSectionTitle}>Custom message</h4>
                    {typeof customMessage === 'string' ? (
                        <p className={styles.bulkCustomMessageText}>{customMessage}</p>
                    ) : (
                        <div className={styles.bulkCustomMessageRich}>
                            <JsonishValue value={customMessage} />
                        </div>
                    )}
                </div>
            )}

            <div className={styles.resultSection}>
                <h4 className={styles.resultSectionTitle}>Job details</h4>
                <dl className={styles.resultMetaGrid}>
                    <dt>Job ID</dt>
                    <dd className={styles.resultMono}>{job?.job_id || '—'}</dd>
                    <dt>Record _id</dt>
                    <dd className={styles.resultMono}>{job?.recordId || '—'}</dd>
                    <dt>Kitchen</dt>
                    <dd>{job?.kitchenName || '—'}</dd>
                    <dt>Admin</dt>
                    <dd>{job?.adminId || '—'}</dd>
                    <dt>Status / step</dt>
                    <dd>
                        {job?.status || '—'} · {job?.currentStep || '—'}
                    </dd>
                    <dt>Progress</dt>
                    <dd>{job?.progress != null ? `${Math.round(job.progress)}%` : '—'}</dd>
                    <dt>Items (step)</dt>
                    <dd>
                        {job?.currentItem != null && job?.totalItems != null
                            ? `${job.currentItem} / ${job.totalItems}`
                            : '—'}
                    </dd>
                    <dt>Last sequence</dt>
                    <dd>{job?.lastSeq != null ? String(job.lastSeq) : '—'}</dd>
                    <dt>Created</dt>
                    <dd>{formatMaybeDate(job?.createdAt)}</dd>
                    <dt>Updated</dt>
                    <dd>{formatMaybeDate(job?.updatedAt)}</dd>
                </dl>
            </div>

            {bulk && (
                <div className={styles.resultSection}>
                    <h4 className={styles.resultSectionTitle}>Bulk products summary</h4>
                    <div className={styles.bulkStatsRow}>
                        {[
                            ['Total', bulk.totalInRequest, null],
                            ['Inserted', bulk.insertedCount, 'success'],
                            ['Failed', bulk.failedCount, 'danger'],
                        ].map(([label, n, variant]) => (
                            <div key={label} className={`${styles.bulkStatChip} ${variant ? styles[`bulkStatChip_${variant}`] : ''}`}>
                                <span className={styles.bulkStatLabel}>{label}</span>
                                <strong className={styles.bulkStatValue}>{n != null ? n : '—'}</strong>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {(items.length > 0 || failedItems.length > 0) && (
                <div className={styles.resultSection}>
                    <div className={styles.tabBar}>
                        <button
                            type='button'
                            className={`${styles.tabBtn} ${activeTab === 'inserted' ? styles.tabBtnActive : ''}`}
                            onClick={() => setActiveTab('inserted')}
                        >
                            Inserted
                            <span className={`${styles.tabBadge} ${styles.tabBadgeSuccess}`}>{items.length}</span>
                        </button>
                        <button
                            type='button'
                            className={`${styles.tabBtn} ${activeTab === 'failed' ? styles.tabBtnActive : ''} ${failedItems.length > 0 ? styles.tabBtnFailed : ''}`}
                            onClick={() => setActiveTab('failed')}
                        >
                            Failed
                            <span className={`${styles.tabBadge} ${failedItems.length > 0 ? styles.tabBadgeDanger : ''}`}>{failedItems.length}</span>
                        </button>
                    </div>

                    {activeTab === 'inserted' && items.length > 0 && (
                    <div className={styles.productList}>
                        {items.map((p, idx) => (
                            <article key={p._id || p.product_sku || idx} className={styles.productCard}>
                                <div className={styles.productCardTop}>
                                    <div className={styles.productCardHeadText}>
                                        <p className={styles.productName}>{p.product_name || '—'}</p>
                                        <p className={styles.productMetaLine}>
                                            <span className={styles.resultMono}>{p.product_sku || '—'}</span>
                                            {' · '}
                                            <span>{p.product_category || '—'}</span>
                                            {' · '}
                                            <span>{formatMoney(p.price)}</span>
                                        </p>
                                    </div>
                                </div>
                                {p.product_description ? (
                                    <p className={styles.productDescription}>{p.product_description}</p>
                                ) : null}
                                <dl className={styles.productKvGrid}>
                                    <dt>Cuisine</dt>
                                    <dd>
                                        <JsonishValue value={p.cuisine} />
                                    </dd>
                                    <dt>Meal type</dt>
                                    <dd>
                                        <JsonishValue value={p.meal_type} />
                                    </dd>
                                    <dt>Serving</dt>
                                    <dd>
                                        {p.serving_temperature || '—'} · {p.serving_size || '—'}
                                    </dd>
                                    <dt>Serving days</dt>
                                    <dd>
                                        <JsonishValue value={p.serving_days} />
                                    </dd>
                                    <dt>Special marking</dt>
                                    <dd>
                                        <JsonishValue value={p.special_marking} />
                                    </dd>
                                    <dt>Allergens</dt>
                                    <dd>
                                        <JsonishValue value={p.allergic_ingredients} />
                                    </dd>
                                    <dt>Ingredients</dt>
                                    <dd>
                                        <JsonishValue value={p.ingredients} />
                                    </dd>
                                    <dt>Extra toppings</dt>
                                    <dd>
                                        <JsonishValue value={p.extra_toppings} />
                                    </dd>
                                    <dt>Product type</dt>
                                    <dd>
                                        <JsonishValue value={p.product_type} />
                                    </dd>
                                    <dt>Qty / week</dt>
                                    <dd>
                                        {p.available_quantity != null ? p.available_quantity : '—'}
                                        {typeof p.is_available_whole_week === 'boolean'
                                            ? ` · whole week: ${p.is_available_whole_week ? 'yes' : 'no'}`
                                            : ''}
                                    </dd>
                                    <dt>Markup %</dt>
                                    <dd>{p.markup_percentage != null ? String(p.markup_percentage) : '—'}</dd>
                                    <dt>Status</dt>
                                    <dd>
                                        {String(p.status)} · {p.status_type || '—'}
                                    </dd>
                                    <dt>Slug</dt>
                                    <dd className={styles.resultMono}>{p.slug || '—'}</dd>
                                    <dt>Created</dt>
                                    <dd>{formatMaybeDate(p.create_at)}</dd>
                                    <dt>Price by device</dt>
                                    <dd>
                                        {p.price_as_per_device && typeof p.price_as_per_device === 'object' ? (
                                            <span>
                                                desktop {formatMoney(p.price_as_per_device.desktop)} · android{' '}
                                                {formatMoney(p.price_as_per_device.android)} · ios{' '}
                                                {formatMoney(p.price_as_per_device.ios)}
                                            </span>
                                        ) : (
                                            '—'
                                        )}
                                    </dd>
                                    <dt>Promotions</dt>
                                    <dd>
                                        <JsonishValue value={p.product_promotion} />
                                    </dd>
                                    <dt>Content confirmation</dt>
                                    <dd>{p.content_confirmation != null ? String(p.content_confirmation) : '—'}</dd>
                                </dl>
                            </article>
                        ))}
                    </div>
                    )}

                    {activeTab === 'inserted' && items.length === 0 && (
                        <p className={styles.tabEmptyNote}>No inserted items to display.</p>
                    )}

                    {activeTab === 'failed' && failedItems.length > 0 && (
                        <div className={styles.failedList}>
                            {failedItems.map((f, idx) => (
                                <div key={f.index ?? idx} className={styles.failedCard}>
                                    <div className={styles.failedCardHeader}>
                                        <span className={styles.failedBadge}>✕</span>
                                        <span className={styles.failedRowLabel}>Row {f.index + 1}</span>
                                    </div>
                                    <ul className={styles.failedErrorList}>
                                        {Array.isArray(f.errors) && f.errors.map((e, ei) => (
                                            <li key={ei} className={styles.failedErrorItem}>
                                                <span className={styles.failedParam}>{e.param}</span>
                                                <span className={styles.failedMsg}>{e.errorMessage}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}

                    {activeTab === 'failed' && failedItems.length === 0 && (
                        <p className={styles.tabEmptyNote}>No failed items.</p>
                    )}
                </div>
            )}

            {job?.result && !bulk && (
                <div className={styles.resultSection}>
                    <h4 className={styles.resultSectionTitle}>Raw result</h4>
                    <pre className={styles.resultJsonBlock}>{JSON.stringify(job.result, null, 2)}</pre>
                </div>
            )}
        </div>
    );
}

function ErrorPanel({ message, onRetry }) {
    return (
        <div className={styles.panelCard}>
            <div className={styles.centerState}>
                <div className={`${styles.stateIcon} ${styles.stateIconError}`}>!</div>
                <p className={styles.centerStateTitle}>Processing failed</p>
                <p className={styles.centerStateSub}>{message || 'Something went wrong while processing this menu.'}</p>
            </div>
        </div>
    );
}

function IdlePanel() {
    const navigate = useNavigate();
    const { setSelectedMenuTab } = useContext(GlobalStateDataContext);

    const goToUploadForm = () => {
        setSelectedMenuTab('agentproduct');
        navigate('/productagent');
    };

    return (
        <div className={styles.panelCard}>
            <div className={styles.centerState}>
                <div className={`${styles.stateIcon} ${styles.stateIconIdle}`}>📋</div>
                <p className={styles.centerStateTitle}>No active job</p>
                <p className={styles.centerStateSub}>
                    This kitchen has no ongoing or recent menu processing job. Upload a menu to get started.
                </p>
                <button
                    type='button'
                    onClick={goToUploadForm}
                    className={styles.primaryButton}
                >
                    Upload a Menu
                </button>
            </div>
        </div>
    );
}

const MenuUploadPage = () => {
    const navigate = useNavigate();
    const { setSelectedMenuTab } = useContext(GlobalStateDataContext);

    const goToUploadForm = useCallback(() => {
        setSelectedMenuTab('agentproduct');
        navigate('/productagent');
    }, [navigate, setSelectedMenuTab]);

    const [selectedJobId, setSelectedJobId] = useState('');
    const [uiState, setUiState] = useState(UI_STATE.IDLE);
    const [job, setJob] = useState({
        job_id: '',
        status: 'idle',
        currentStep: '',
        progress: 0,
        currentItem: null,
        totalItems: null,
        message: '',
        recordId: '',
        adminId: '',
        lastSeq: null,
        createdAt: null,
        updatedAt: null,
        result: null,
    });
    const [pollingJobId, setPollingJobId] = useState('');
    const connectionStatus = 'polling';
    const [errorMessage, setErrorMessage] = useState('');
    const [detailLoading, setDetailLoading] = useState(false);

    const activeJobIdRef = useRef('');
    const throttleRef = useRef({ timer: null, payload: null, source: 'polling' });

    const [jobsList, setJobsList] = useState(null);
    const jobsListRef = useRef(jobsList);
    jobsListRef.current = jobsList;

    const mergeJobIntoList = useCallback((payload) => {
        const id = payload?.job_id || payload?.jobId;
        if (!id) return;
        setJobsList((prev) => {
            if (!prev) return prev;
            const idx = prev.findIndex((j) => String(j.job_id || j._id) === String(id));
            if (idx < 0) return prev;
            const merged = { ...prev[idx], ...payload };
            if (shallowEqualJobRow(prev[idx], merged)) return prev;
            const next = [...prev];
            next[idx] = merged;
            return next;
        });
    }, []);

    const refreshJobsList = useCallback(() => {
        getMenuJobsListAPI({}).then((response) => {
            if (response.hasError) {
                setJobsList([]);
                return;
            }
            const jobs = extractJobsArrayFromListResponse(response.data);
            setJobsList(jobs);
        });
    }, []);

    useEffect(() => {
        refreshJobsList();
    }, [refreshJobsList]);

    const sidebarJobs = useMemo(
        () => (Array.isArray(jobsList) ? jobsList : []),
        [jobsList],
    );

    const persistCachedJob = useCallback((jobId, payload) => {
        if (typeof window === 'undefined' || !jobId) return;
        try {
            const cachedText = window.localStorage.getItem(STORAGE_KEYS.jobsCache);
            const cached = cachedText ? JSON.parse(cachedText) : {};
            cached[jobId] = payload;
            window.localStorage.setItem(STORAGE_KEYS.jobsCache, JSON.stringify(cached));
        } catch (error) {
            // Ignore storage errors to keep upload flow resilient.
        }
    }, []);

    const readCachedJob = useCallback((jobId) => {
        if (typeof window === 'undefined' || !jobId) return null;
        try {
            const cachedText = window.localStorage.getItem(STORAGE_KEYS.jobsCache);
            const cached = cachedText ? JSON.parse(cachedText) : {};
            return normalizeJobPayload(cached[jobId], '');
        } catch (error) {
            return null;
        }
    }, []);

    const flushThrottledPayload = useCallback(
        (force = false) => {
            const queued = throttleRef.current.payload;
            if (!queued) return;
            if (!force && throttleRef.current.timer) return;

            const normalized = normalizeJobPayload(queued.data, '');
            if (!normalized) return;

            activeJobIdRef.current = normalized.job_id || activeJobIdRef.current;
            setJob(normalized);
            persistCachedJob(selectedJobId, normalized);
            mergeJobIntoList(queued.data);

            const kitchenStatus = deriveKitchenStatus(normalized);

            if (kitchenStatus === 'failed') {
                setUiState(UI_STATE.ERROR);
                setErrorMessage(normalized.message || 'AI processing failed.');
                setPollingJobId('');
                return;
            }

            if (kitchenStatus === 'completed') {
                setUiState(UI_STATE.COMPLETED);
                setPollingJobId('');
                return;
            }

            setUiState(UI_STATE.PROCESSING);
        },
        [mergeJobIntoList, persistCachedJob, selectedJobId],
    );

    const queuePayload = useCallback(
        (data, source = 'polling', force = false) => {
            throttleRef.current.payload = { data, source };
            throttleRef.current.source = source;

            if (force) {
                if (throttleRef.current.timer) {
                    clearTimeout(throttleRef.current.timer);
                    throttleRef.current.timer = null;
                }
                flushThrottledPayload(true);
                return;
            }

            if (!throttleRef.current.timer) {
                throttleRef.current.timer = setTimeout(() => {
                    throttleRef.current.timer = null;
                    flushThrottledPayload(true);
                }, 300);
            }
        },
        [flushThrottledPayload],
    );

    const resetToIdle = useCallback(() => {
        setUiState(UI_STATE.IDLE);
        setErrorMessage('');
        setJob({
            job_id: '',
            status: 'idle',
            currentStep: '',
            progress: 0,
            currentItem: null,
            totalItems: null,
            message: '',
            recordId: '',
            adminId: '',
            lastSeq: null,
            createdAt: null,
            updatedAt: null,
            result: null,
        });
        activeJobIdRef.current = '';
        setPollingJobId('');
    }, []);

    const handleJobSelect = useCallback((jobId) => {
        setSelectedJobId(jobId);
    }, []);

    const isSelectedInList = useMemo(() => {
        if (jobsList === null || !jobsList.length || !selectedJobId) return false;
        return jobsList.some((j) => String(j.job_id || j._id) === String(selectedJobId));
    }, [jobsList, selectedJobId]);

    /** Resolve selection when the list loads; persist last selected id. Job details load via GET /status below. */
    useEffect(() => {
        if (jobsList === null) return;

        if (!jobsList.length) {
            setSelectedJobId('');
            resetToIdle();
            return;
        }

        const ids = jobsList.map((j) => String(j.job_id || j._id));
        const resolvedId =
            selectedJobId && ids.includes(selectedJobId) ? selectedJobId : ids[0];

        if (resolvedId !== selectedJobId) {
            setSelectedJobId(resolvedId);
            return;
        }

        if (typeof window !== 'undefined' && resolvedId) {
            window.localStorage.setItem(STORAGE_KEYS.selectedJob, resolvedId);
        }
    }, [jobsList, selectedJobId, resetToIdle]);

    useLayoutEffect(() => {
        if (isSelectedInList && selectedJobId) {
            setDetailLoading(true);
        } else {
            setDetailLoading(false);
        }
    }, [isSelectedInList, selectedJobId]);

    /** On list item selection (or initial default), GET /status/:job_id and drive the right panel. */
    useEffect(() => {
        if (!isSelectedInList || !selectedJobId) {
            setDetailLoading(false);
            return undefined;
        }

        let cancelled = false;
        setPollingJobId('');
        setErrorMessage('');

        const syncPollingForNormalized = (normalized) => {
            if (!normalized?.job_id) return;
            if (deriveKitchenStatus(normalized) === 'processing') {
                activeJobIdRef.current = normalized.job_id;
                setPollingJobId(normalized.job_id);
            } else {
                setPollingJobId('');
            }
        };

        (async () => {
            try {
                const response = await getMenuStatusAPI(selectedJobId);
                if (cancelled) return;

                if (response.hasError) {
                    const jobRow =
                        jobsListRef.current?.find(
                            (j) => String(j.job_id || j._id) === String(selectedJobId),
                        ) || null;
                    const fromList = normalizeJobPayload(jobRow, jobRow?.kitchen_id || '');
                    if (fromList?.job_id) {
                        queuePayload(jobRow, 'polling', true);
                        syncPollingForNormalized(fromList);
                        setDetailLoading(false);
                        return;
                    }
                    const cachedPayload = readCachedJob(selectedJobId);
                    if (cachedPayload?.job_id) {
                        queuePayload(cachedPayload, 'polling', true);
                        syncPollingForNormalized(cachedPayload);
                        setDetailLoading(false);
                        return;
                    }
                    setUiState(UI_STATE.ERROR);
                    setErrorMessage(response.message || 'Failed to load job status.');
                    setDetailLoading(false);
                    return;
                }

                const payload = unwrapApiResponse(response.data);
                if (cancelled) return;
                queuePayload(payload, 'polling', true);
                const normalized = normalizeJobPayload(payload, '');
                syncPollingForNormalized(normalized);
                setDetailLoading(false);
            } catch (error) {
                if (cancelled) return;
                setDetailLoading(false);
                setUiState(UI_STATE.ERROR);
                setErrorMessage(error?.message || 'Could not load job status.');
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [selectedJobId, isSelectedInList, queuePayload, readCachedJob]);

    useEffect(() => {
        const throttleState = throttleRef.current;
        return () => {
            if (throttleState.timer) clearTimeout(throttleState.timer);
        };
    }, []);

    useEffect(() => {
        if (!pollingJobId) return undefined;
        let cancelled = false;
        let timeoutId = null;

        const pollStatus = async () => {
            if (cancelled) return;
            try {
                const response = await getMenuStatusAPI(pollingJobId);
                if (cancelled) return;
                if (response.hasError) throw new Error(response.message || 'Status polling failed');
                const payload = unwrapApiResponse(response.data);
                queuePayload(payload, 'polling', true);
                if (!cancelled) {
                    timeoutId = setTimeout(pollStatus, 3000);
                }
            } catch (error) {
                if (cancelled) return;
                setUiState(UI_STATE.ERROR);
                setErrorMessage(error?.message || 'Could not retrieve status updates.');
            }
        };

        pollStatus();
        return () => {
            cancelled = true;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [pollingJobId, queuePayload]);

    return (
        <section className={styles.page}>
            <div className={styles.frame}>
                <JobsSidebar
                    jobs={sidebarJobs}
                    selectedJobId={selectedJobId}
                    onJobSelect={handleJobSelect}
                    connectionStatus={connectionStatus}
                    listLoading={jobsList === null}
                />

                <main className={styles.main}>
                    <header className={styles.mainHeader}>
                        <p className={styles.kicker}>AI Menu Processor</p>
                        <h1 className={styles.mainTitle}>Menu Processing Jobs</h1>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '10px', flexWrap: 'wrap' }}>
                            <button
                                type='button'
                                onClick={goToUploadForm}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '8px 16px',
                                    background: 'linear-gradient(135deg, #2563eb, #0ea5e9)',
                                    color: '#fff',
                                    borderRadius: '8px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                }}
                            >
                                + Upload New Menu
                            </button>
                        </div>
                    </header>

                    <section className={styles.contentArea}>
                        {detailLoading && (
                            <div className={styles.panelCard}>
                                <div className={styles.centerState}>
                                    <RippleLoader />
                                    <p className={styles.centerStateTitle} style={{ marginTop: '16px' }}>
                                        Loading job…
                                    </p>
                                    <p className={styles.centerStateSub}>Fetching the latest status from the server.</p>
                                </div>
                            </div>
                        )}

                        {!detailLoading && uiState === UI_STATE.IDLE && <IdlePanel />}

                        {!detailLoading && uiState === UI_STATE.PROCESSING && (
                            <ProcessingPanel job={job} connectionStatus={connectionStatus} />
                        )}

                        {!detailLoading &&
                            (uiState === UI_STATE.COMPLETED ||
                                (uiState === UI_STATE.ERROR && job?.result?.bulk_products)) && (
                            <CompletedPanel job={job} onReset={resetToIdle} />
                        )}

                        {!detailLoading &&
                            uiState === UI_STATE.ERROR &&
                            !job?.result?.bulk_products && (
                            <ErrorPanel message={errorMessage || job.message} onRetry={refreshJobsList} />
                        )}
                    </section>
                </main>
            </div>
        </section>
    );
};

export default MenuUploadPage;
